import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../auth/AuthService';
import API_URL from '../config/ApiUrl';
const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const refreshUserStats = useCallback(async (showLoading = false) => {
    if (!isAuthenticated || !user) return;
    
    if (showLoading) {
      setStatsLoading(true);
    }
    
    try {
      const response = await fetch(`${API_URL}/api/user/stats/header`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const stats = await response.json();
        console.log('STATS', stats)
        setUserStats(stats);
      } else {
        console.error('Failed to fetch user stats');
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      if (showLoading) {
        setStatsLoading(false);
      }
    }
  }, [isAuthenticated, user]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check localStorage for cached user data
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          setUser(JSON.parse(cachedUser));
          setIsAuthenticated(true);
        }

        // Then verify with backend
        const userData = await authApi.getCurrentUser();
        setUser(userData.user);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData.user));
        
        // Fetch user stats after successful auth
        // Note: We'll call refreshUserStats after the state updates
      } catch (error) {
        // User not logged in or session expired
        setUser(null);
        setIsAuthenticated(false);
        setUserStats(null);
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fetch stats when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && user && !loading) {
      refreshUserStats(true); // Show loading on initial fetch
    }
  }, [isAuthenticated, user, loading, refreshUserStats]);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setUserStats(null); // Clear stats on logout
      localStorage.removeItem('user');
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  return (
    <UserContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      userStats,        // ← ADD THIS LINE
      statsLoading,     // ← ADD THIS LINE
      login,
      logout,
      updateUser,
      refreshUserStats
    }}>
      {children}
    </UserContext.Provider>
  );
};